<template>
    <div class="account-rule">
        <div class="rule-header">
            <div class="rule-header-content">
                <StatusPill v-if="rule.status" :status-label="rule.status" :status="ruleStatusColor" />

                <div class="rule-name">
                    {{ rule.name }}
                </div>
            </div>

            <div v-if="isDeleted" class="rule-actions">
                <Button data-testid="connect__restore-rule-button" @click="restoreRule">
                    <icon type="ios-undo" />
                    Restore rule
                </Button>
            </div>

            <div v-else-if="!accountIsDeleted" class="rule-actions">
                <a data-testid="connect__edit-rule-button" @click="showEditRuleModal = true">
                    <icon type="md-create" />
                    Edit rule
                </a>

                <a data-testid="connect__duplicate-rule-button" @click="duplicateRule">
                    <icon type="md-copy" />
                    Duplicate rule
                </a>

                <a data-testid="connect__delete-rule-button" @click="onClickDeleteRule">
                    <Icon type="md-trash" />
                    Delete rule
                </a>

                <a data-testid="connect__move-rule-button" class="rule-drag-handle" @mousedown="onClickMoveRule">
                    <Icon type="ios-move" />
                    Move rule
                </a>
            </div>
        </div>

        <div :class="{ 'rule-content--disabled': isDeleted || accountIsDeleted }">
            <div class="rule-variants">
                <div v-if="getSelectedNumberOfVariants(platformAccount.name, rule.name)">
                    <strong>
                        You have {{ getSelectedNumberOfVariants(platformAccount.name, rule.name) }} variants selected.
                    </strong>
                    <br />
                    You can change or add to your current selection.
                </div>

                <div v-else>
                    <strong>You don't have any variants selected.</strong>
                    <br />
                    Please select a variant to create a signal.
                </div>
                <a
                    class="select-variants-link"
                    data-testid="connect__select-variants-button"
                    @click="onClickSelectVariants"
                    >Select variants</a
                >
            </div>

            <div v-if="selectedNumberOfVariants" class="rule-signals">
                <div class="signals-header signals-row">
                    <span><strong>Signal</strong></span>
                    <span class="right"><strong>Activity Status</strong></span>
                </div>

                <div v-if="signals.length" class="signals-row">
                    <div class="signal-content" @click="editSignal">
                        <p v-for="(signal, signalIndex) in signals" :key="signalIndex">
                            <span :innerHTML="getSignalDescription(signal)" />
                            <template v-if="signalIndex !== signals.length - 1">
                                <br />
                                <strong><span>AND</span></strong>
                                <br />
                            </template>
                        </p>
                    </div>

                    <span class="right signal-status">
                        <StatusPill :status-label="signalStatusLabel" :status="signalStatusColor" />
                    </span>
                </div>

                <div v-else>
                    <p><strong>You don't have any signals.</strong></p>
                    <p>Manage how your variants are delivered by creating custom signals.</p>
                </div>
            </div>

            <div v-if="selectedNumberOfVariants" class="signal-add">
                <a data-testid="connect__add-signal-button" @click="addSignal">
                    <icon size="24" type="ios-add-circle-outline" />
                    {{ signals.length ? "Add new condition" : "Add new signal" }}
                </a>
            </div>
        </div>

        <AccountRuleFormModal
            v-if="showEditRuleModal"
            :platform-account="platformAccount"
            :rule="rule"
            @close="showEditRuleModal = false"
        />

        <AccountSignalFormModal
            v-if="showSignalModal"
            :add-condition="addCondition"
            :rule-signals="signals"
            @saveSignals="updateRuleSignals"
            @close="closeSignalModal"
        />

        <AlertModal
            v-if="showDeleteRuleModal"
            v-model="showDeleteRuleModal"
            header="Delete rule"
            title="Are you sure?"
            content="You can see and restore the deleted rule by making the deleted rules and accounts visible."
            ok-text="Delete Rule"
            :on-ok="deleteRule"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import AlertModal from "@/components/Modal/Alert.vue";
import AccountRuleFormModal from "@/Connect/components/Accounts/PlatformAccountRuleEdit.vue";
import AccountSignalFormModal from "@/Connect/components/Accounts/PlatformAccountRuleSignal.vue";
import StatusPill, { StatusMap } from "@/Connect/components/Accounts/StatusPill.vue";

import type { PlatformAccount, Rule, Signal } from "@/Connect/types";
import { useConnectStore } from "@/Connect/store";
import getSignalDescription from "@/Connect/utils/getSignalDescription";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

type Props = {
    platformAccount: PlatformAccount;
    rule: Rule;
};

const connectStore = useConnectStore();

const { platformAccount, rule } = defineProps<Props>();

const emit = defineEmits<(e: "show-variants-select") => void>();

/**
 * LOCAL STATE
 */
const showEditRuleModal = ref(false);
const showSignalModal = ref(false);
const showDeleteRuleModal = ref(false);
const addCondition = ref(false);

/**
 * COMPUTED
 */
const { getSelectedNumberOfVariants } = storeToRefs(connectStore);

const signals = computed(() => rule.signals);
const ruleStatusColor = computed(() => StatusMap[rule.status]);
const accountIsDeleted = computed(() => platformAccount.status === "deleted");
const isDeleted = computed(() => rule.status === "deleted");
const signalStatusColor = computed(() => (rule?.isRuleActiveBySignals ? "green" : "gray"));
const signalStatusLabel = computed(() =>
    rule?.status === "draft" ? "Draft" : rule?.isRuleActiveBySignals ? "Triggered" : "Not triggered"
);
const ga4 = useGA4Tracking();

const selectedNumberOfVariants = computed(() => getSelectedNumberOfVariants.value(platformAccount.name, rule.name));

/**
 * METHODS
 */
function duplicateRule() {
    ga4.trackClickEvent({
        interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
        interactionText: GA4.CLICK_INTERACTION_TEXTS.DUPLICATE_RULE
    });
    connectStore.duplicateRule(platformAccount.name, rule);
}

function deleteRule() {
    connectStore.deleteRule(platformAccount.name, rule.name);
}

function restoreRule() {
    ga4.trackClickEvent({
        interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
        interactionText: GA4.CLICK_INTERACTION_TEXTS.RESTORE_RULE
    });
    connectStore.restoreRule(platformAccount.name, rule.name);
}

function addSignal() {
    showSignalModal.value = true;
    addCondition.value = true;
}

function editSignal() {
    showSignalModal.value = true;
}

function closeSignalModal() {
    showSignalModal.value = false;
    addCondition.value = false;
}

function onClickDeleteRule() {
    ga4.trackClickEvent({
        interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
        interactionText: GA4.CLICK_INTERACTION_TEXTS.DELETE_RULE
    });
    showDeleteRuleModal.value = true;
}

function onClickMoveRule() {
    ga4.trackClickEvent({
        interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
        interactionText: GA4.CLICK_INTERACTION_TEXTS.MOVE_RULE
    });
}

function onClickSelectVariants() {
    ga4.trackClickEvent({
        interactionObject: GA4.CLICK_INTERACTION_OBJECTS.LINK,
        interactionText: GA4.CLICK_INTERACTION_TEXTS.SELECT_VARIANTS
    });
    emit("show-variants-select");
}

function updateRuleSignals(newSignals: Partial<Signal>[]) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    connectStore.updateRuleSignals(platformAccount.name, rule.name, newSignals);
}
</script>

<style lang="scss" scoped>
.account-rule {
    background-color: #fff;

    padding: 16px;
}

.rule-content--disabled {
    opacity: 0.5;
    pointer-events: none;
}

.rule-header {
    padding: 16px 0;
    border-bottom: 1px solid var(--wpp-grey-color-400);

    display: flex;
    align-items: center;
    justify-content: space-between;
}

.rule-header-content {
    display: flex;
    gap: 10px;
}

.rule-name {
    font-size: 16px;
    font-weight: 600;
}

.rule-actions {
    display: flex;
    gap: 5px;

    a:hover {
        text-decoration: underline;
    }
}

.rule-variants {
    padding: 16px 0;
    border-bottom: 1px solid var(--wpp-grey-color-300);
}

.select-variants-link {
    padding-top: 8px;
    padding-left: 16px;

    display: inline-block;

    &:hover {
        text-decoration: underline;
    }
}

.rule-signals {
    padding: 16px 0;
    border-bottom: 1px solid var(--wpp-grey-color-400);

    display: flex;
    flex-direction: column;
    gap: 8px;
}

.signals-row {
    display: flex;
    justify-content: space-between;
}

.right {
    flex-basis: 150px;
}

.signals-header {
    padding-bottom: 13px;
    border-bottom: 1px solid var(--wpp-grey-color-300);
}

.signal-content {
    display: flex;
    flex-direction: column;

    cursor: pointer;
}

.signal-add {
    padding-top: 16px;
    padding-left: 24px;

    a {
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
}

.signal-status {
    display: flex;
    align-items: center;
}
</style>
